import classNames from 'classnames';
import { useRouter } from 'next/router';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Serializer } from '@typedream/components';
import * as Dialog from '@radix-ui/react-dialog';
import { CircleFill } from '@styled-icons/bootstrap';
import { Close } from '@styled-icons/material';
import { useSetDreamEditorContent } from '@typedream/dream-editor';
import { Button, Text } from '@typedream/ui';
import { useSpaceAndPageIDFromRouter } from '@/data/space/hooks';
import Popup from '@/sharedComponents/TextEditor/components/Popup';
import { TextEditorContext } from '@/sharedComponents/TextEditor/context/TextEditorContext';
import flags from '@/store/flags';
import {
  useFeatureFlag,
  useGetPageQuery,
  useGetVersionContentQuery,
  useGetVersionListQuery,
} from '@/store';
import { formatDateString, formatTimeString } from '@/utils/dateFormatter';
import ButtonOutline from '@/sharedComponents/Button/ButtonOutline';
import LoadingOnComponent from '../LoadingOnComponent';
import { VersionHistoryPopupContext, VersionHistoryDispatchContext } from './context';
import styles from './VersionHistoryPopup.module.css';

import {
  StyledDialogFooter,
  StyledDialogHeader,
  StyledDialogModal,
  StyledDialogModalContent,
  StyledDialogOverlay,
} from '../Dialog';

type VersionHistoryItemProps = {
  item: {
    file_name: string;
    time: string;
  };
  // eslint-disable-next-line react/require-default-props
  title?: string;
  // eslint-disable-next-line react/require-default-props
  subtitle?: string;
  // eslint-disable-next-line react/require-default-props
  isSelected?: boolean;
  // eslint-disable-next-line react/require-default-props
  isCurrentActive?: boolean;
  onClick: () => void;
};

function VersionHistoryItem({
  item,
  isSelected,
  onClick,
  title,
  subtitle,
  isCurrentActive,
}: VersionHistoryItemProps) {
  return (
    <div
      role="button"
      tabIndex={0}
      onKeyDown={() => {
        /* DO NOTHING */
      }}
      className={classNames(styles.versionItemContainer, {
        [styles.versionItemContainerSelected]: isSelected,
      })}
      onClick={onClick}
    >
      <Text as="div" size="sm" weight="semibold">
        {title ?? formatDateString(item.time)}
        {isCurrentActive && <CircleFill size={10} className={styles.circleFillIcon} />}
      </Text>
      <Text as="div" size="xs" weight="medium" variant="mute">
        {subtitle ?? formatTimeString(item.time)}
      </Text>
    </div>
  );
}

export function VersionHistoryPopup() {
  const isVersionHistoryPopupOpen = useContext(VersionHistoryPopupContext);
  const setIsVersionHistoryPopupOpen = useContext(VersionHistoryDispatchContext);
  const setDreamEditorValue = useSetDreamEditorContent();
  const { setInitialValue } = useContext(TextEditorContext);
  const [spaceID, pageID] = useSpaceAndPageIDFromRouter();
  const { data: page } = useGetPageQuery([spaceID as string, pageID as string]);
  const router = useRouter();
  const { slug } = router.query;
  const {
    data: versionHistoryData,
    isLoading,
    isError,
  } = useGetVersionListQuery([pageID as string, spaceID as string]);

  const isDreamEditorEnabled = useFeatureFlag(flags.DreamEditor);

  const [selectedVersion, setSelectedVersion] = useState<{
    file_name: string;
    time: string;
  } | null>(null);

  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);

  const { data: selectedVersionContent, isLoading: isLoadingVersionContent } =
    useGetVersionContentQuery([selectedVersion?.file_name ?? '', spaceID as string], {
      skip: selectedVersion == null,
    });

  const content = useMemo(
    () => selectedVersionContent?.data ?? page?.content,
    [selectedVersionContent, selectedVersion, slug, pageID, page]
  );

  useEffect(() => {
    setSelectedVersion(null);
  }, [pageID, spaceID]);

  const onRestoreVersion = () => {
    if (isDreamEditorEnabled) {
      setDreamEditorValue(selectedVersionContent?.data);
    } else {
      setInitialValue(selectedVersionContent?.data);
    }
    setIsConfirmationDialogOpen(false);
    setIsVersionHistoryPopupOpen(false);
  };

  const onBack = () => {
    setIsConfirmationDialogOpen(false);
    setIsVersionHistoryPopupOpen(true);
  };

  const onShowConfirmation = () => {
    setIsVersionHistoryPopupOpen(false);
    setIsConfirmationDialogOpen(true);
  };

  return (
    <>
      <Popup
        width="80vw"
        height="80vh"
        open={isVersionHistoryPopupOpen}
        setopen={setIsVersionHistoryPopupOpen}
      >
        <div className={styles.container}>
          <div className={styles.header}>
            <h1 className="is-size-5 with-text-color has-text-weight-medium">Version history</h1>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              Current Version -{' '}
              {selectedVersion ? (
                <>
                  {formatDateString(selectedVersion.time)} {formatTimeString(selectedVersion.time)}{' '}
                  <Button variant="default" type="submit" onClick={onShowConfirmation}>
                    Restore Version
                  </Button>
                </>
              ) : (
                'Now'
              )}
            </div>
          </div>
          <div className={styles.contentContainer}>
            <div style={{ flex: 1, overflow: 'auto', position: 'relative' }}>
              <div
                style={{
                  position: 'absolute',
                  zIndex: 2,
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                }}
              />
              <Serializer
                rootClassName={classNames(styles.contentInner, 'typedream content')}
                node={(content?.[0] || { children: [] }) as any}
              />
              <LoadingOnComponent loading={isLoadingVersionContent} />
            </div>
            <div className={styles.contentListContainer}>
              <h2 className={styles.versionListTitle}>Version Lists</h2>
              <VersionHistoryItem
                item={{
                  file_name: '',
                  time: page?.content_updated_at,
                }}
                title="Current Version"
                onClick={() => setSelectedVersion(null)}
                isSelected={selectedVersion == null}
                isCurrentActive
              />
              <hr />
              {isLoading && (
                <div className={styles.versionListLoadingWrapper}>
                  <Text as="div">Fetching version list</Text>
                  <div className="loader-big loader is-loading with-accent-border-color" />
                </div>
              )}
              {isError && (
                <div className={styles.versionListLoadingWrapper}>
                  <Text as="div" variant="error">
                    Oops, something went wrong
                  </Text>
                </div>
              )}
              {!isLoading &&
                versionHistoryData?.data &&
                versionHistoryData?.data.map((d) => (
                  <VersionHistoryItem
                    key={d.file_name}
                    item={d}
                    onClick={() => setSelectedVersion(d)}
                    isSelected={d.file_name === selectedVersion?.file_name}
                  />
                ))}
            </div>
          </div>
        </div>
      </Popup>
      <Dialog.Root
        open={isConfirmationDialogOpen}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            onBack();
          }
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay asChild>
            <StyledDialogOverlay />
          </Dialog.Overlay>
          <Dialog.Content>
            <StyledDialogModal height={250} width={450}>
              <StyledDialogHeader>
                <div
                  className="is-flex is-align-items-center is-justify-content-space-between"
                  style={{ width: '100%' }}
                >
                  <Text as="div" size="xl" variant="main">
                    Restore this version?
                  </Text>
                  <Dialog.Close asChild>
                    <Close size={24} style={{ cursor: 'pointer' }} />
                  </Dialog.Close>
                </div>
              </StyledDialogHeader>
              <StyledDialogModalContent>
                <Text as="div" size="md" variant="main">
                  Your current work will revert to version from{' '}
                  {formatDateString(selectedVersion?.time)}{' '}
                  {formatTimeString(selectedVersion?.time)}
                </Text>
              </StyledDialogModalContent>
              <StyledDialogFooter>
                <div className={styles.dialogButtonContainer}>
                  <ButtonOutline
                    type="button"
                    onClick={() => {
                      onBack();
                    }}
                  >
                    Back
                  </ButtonOutline>
                  <Button variant="default" type="submit" onClick={onRestoreVersion}>
                    Restore
                  </Button>
                </div>
              </StyledDialogFooter>
            </StyledDialogModal>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
